/**
 * Admin Home Groups List Hook (URQL)
 *
 * Query hook for fetching the list of home groups in admin.
 */

import { useQuery } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { AdminHomeGroupListItem, AdminHomeGroupsFilter } from "../types";

interface AdminHomeGroupsResult {
  groups: AdminHomeGroupListItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// QUERY
// =============================================================================

const AdminHomeGroupsDocument = `
  query AdminHomeGroups($filter: AdminHomeGroupsFilter) {
    adminHomeGroups(filter: $filter) {
      id
      name
      description
      location
      isActive
      isOpen
      memberCount
      leader {
        id
        firstName
        lastName
        username
      }
      coLeader {
        id
        firstName
        lastName
        username
      }
      schedules {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

export function useAdminHomeGroups(
  filter?: AdminHomeGroupsFilter
): AdminHomeGroupsResult {
  const [result, reexecute] = useQuery<{
    adminHomeGroups: AdminHomeGroupListItem[];
  }>({
    query: AdminHomeGroupsDocument,
    variables: { filter: filter && Object.keys(filter).length ? filter : undefined },
  });

  // Track errors
  if (result.error) {
    errorTracking.addBreadcrumb({
      category: "admin.homeGroups",
      message: "Failed to load home groups list",
      level: "error",
    });
  }

  return {
    groups: result.data?.adminHomeGroups ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}
